import { getNameFunctionType } from 'app/services/localization';
import { decodeOrThrow } from 'app/utils/ioTs';
import { chain, Either } from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/pipeable';
import { List, Map, Set } from 'immutable';
import * as t from 'io-ts';

export interface IRawEnum<Id extends string> {
  enum_constant: Id;
}

export const RawEnum: t.Type<IRawEnum<string>> = t.interface({
  enum_constant: t.string,
});

export interface IBareEnum<Id extends string> {
  id: Id;
}

export class EnumClass<Id extends string, R extends IRawEnum<Id>> {
  public readonly id: Id;
  public readonly metadata: R;

  public constructor(metadata: R) {
    this.id = metadata.enum_constant;
    this.metadata = metadata;
  }

  public toString() {
    return this.id;
  }

  public toJSON() {
    return this.id;
  }
}

type IEnumConstructor<Id extends string, R extends IRawEnum<Id>, T extends EnumClass<Id, R>> = new (item: R) => T;

export function build<Id extends string, R extends IRawEnum<Id>, T extends EnumClass<Id, R>>(
  typeName: string,
  data: R[],
  Enum: IEnumConstructor<Id, R, T>,
) {
  const idSet = Set<string>(data.map(item => item.enum_constant));
  const isId = (x: unknown): x is Id => typeof x === 'string' && idSet.has(x);
  const list = List(data.map(item => new Enum(item)));
  const map = Map(list.map((item): [Id, T] => [item.id, item]));

  const getById = (id: Id) => {
    const maybeValue = map.get(id);
    if (maybeValue === undefined) {
      throw new Error(`${id} was not found in ${typeName} value map`);
    }
    return maybeValue;
  };

  const codec = new t.Type<T, string, unknown>(
    typeName,
    (e): e is T => e instanceof Enum,
    (e, c) =>
      pipe(
        t.string.decode(e),
        chain(
          (str): Either<t.Errors, Id> =>
            isId(str) ? t.success(str) : t.failure(str, c, `Expected ${str} to be a ${typeName} Id`),
        ),
        chain(right => {
          const enumVal = getById(right);
          return enumVal ? t.success(enumVal) : t.failure(right, c, `Unable to find enum value for ${enumVal}.`);
        }),
      ),
    e => e.toJSON(),
  );
  const marshaller = {
    deserialize: (e: unknown) => {
      return decodeOrThrow(codec, e, errors => {
        throw new Error(`Failed to deserialize ${typeName} from ${JSON.stringify(e)}:\n ${errors.join('\n')}`);
      });
    },
    serialize: (e: EnumClass<Id, R>) => {
      if (!codec.is(e)) {
        throw new Error(`Unable to serialize ${typeName} to string: ${JSON.stringify(e)} is not ${typeName}`);
      }
      return codec.encode(e);
    },
  };

  return { list, getById, marshaller, isId, codec };
}

export interface ITranslationTable {
  [key: string]: getNameFunctionType;
}

export interface IEnumData<Id extends string> {
  raw: Array<IRawEnum<Id>>;
  list: List<IBareEnum<Id>>;
  endpoint: string;
  omitDataFields: string[];
}

export interface IAllEnumData {
  [key: string]: IEnumData<string>;
}

export default EnumClass;
